import DPO from 'dpo8core';
import TraceImageToShape from 'TraceImageToShape';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import useSaveToYourAccount from '@app/hooks/use-save-to-your-account';
import Mousetrap from 'mousetrap';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  processFinishedArtwork,
  resetArtworkInfo,
  createContext,
  finishArtwork,
  processError,
  addProjectWarning,
  removeProjectWarning,
  resetArtworkImage,
} from '@app/actions/artworkDataActions';
import {
  requestLowResImage,
  getImageByUrl,
  resetCustomizeData,
  setSnapToEdgeVisible,
  setEditorZoom,
  cleanupActions,
  updateCustomizeActions,
  setResizeParams,
  setScaleParams,
  setPanelSizes,
  setImageNotGenerated,
  addWorkingAreaWarning,
  removeWorkingAreaWarning,
  applyCustomSizedShape,
  resetCustomSizedArtwork,
  setBgParams,
  processJob,
  requestImgBgRemoved,
  addWorkingAreaMessage,
} from '@app/actions/customizeActions';
import { setWhiteInkOptionsNeeded, resetWhiteInkOptionsData } from '@app/actions/whiteInkOptionsActions';
import SplitActionChain from '@app/actions-chain/SplitActionChain';
import { startLoading, endLoading } from '@app/actions/spinnerActions';
import {
  CUSTOMIZE_VIEW,
  APPLY_CODES_VIEW,
  WHITE_INK_OPTIONS_VIEW,
  WORKING_AREA_WARNING_STRETCHED_CODE,
  WORKING_AREA_WARNING_CUSTOM_SHAPE_CODE,
  WORKING_AREA_WARNING_HPI_WHITE_CODE,
  WORKING_AREA_WARNING_CUSTOM_SHAPE_HPI_WHITE_CODE,
  UPLOAD_ART_VIEW,
  CORNER_TYPES,
  LOGIN_SIGNUP_VIEW,
} from '@app/constants';
import { setFooterConfig } from '@app/actions/footerActions';
import { getAuthorizationStatus } from '@app/actions/authActions';
import { inject } from '@app/providers/QAU.ContextProvider';
import { navigate, navigateToView, updateMatrixState } from '@app/router';
import debounce from '@app/utils/debounce';
import {
  parseError,
  loadProject,
  getDOMElRect,
  isMobileMode,
  isApplyCodesViewAvailable,
  isImageFitDisabled,
  getOriginalSourceValue,
  isCustomShapeFlow,
  isStickerFlow,
  isCustomShape,
  isPolygonShape,
  findRmbgImg,
  checkForWhiteIfNeeded,
  checkShapeIsEquilateral,
  getDefaultCornerRadius,
  roundNum,
  getPanelFields,
  isSaveUploadArtworkToMyAccount,
  isCatalogSheetFlow,
} from '@app/utils/utils';
import {
  getWhiteTreatmentHint,
  getOriginalNameFromHint,
  getProjectHintValue,
  getProjectShape,
  getArtworkOriginalSizes,
  setArtworkOriginalSizes,
  setProjectContourCutOption,
  isCreatedFromDielineShape,
  getWhiteSpotColorDetected,
} from '@app/utils/hints';
import { getCustomStickerSize, getCustomStickerImgSize } from '@app/utils/customSticker';
import { getWorkingAreaWarningsList } from '@app/utils/help';
import { toTwips, fromTwips, inchesToTwips, getCurrentMeasurementLocale } from '@app/utils/measurement';

import WorkingAreaMessages from './working-area-messages/WorkingAreaMessages';
import WorkingAreaWarnings from './working-area-warnings/WorkingAreaWarnings';
import SideToolPanel from './side-tool-panel/SideToolPanel';
import TopToolPanel from './top-tool-panel/TopToolPanel';
import WorkingArea from './working-area/WorkingArea';
import './CustomizeView.scss';

const CustomizeView = ({
  updateCustomizeActions,
  processFinishedArtwork,
  whiteInkOptionsNeeded,
  removeWorkingAreaWarning,
  resetCustomSizedArtwork,
  applyCustomSizedShape,
  addWorkingAreaWarning,
  addWorkingAreaMessage,
  createContext,
  requestLowResImage,
  setSnapToEdgeVisible,
  shortcutActions,
  setEditorZoom,
  setFooterConfig,
  cleanupActions,
  updateContext,
  pendingAction,
  getImageByUrl,
  finishArtwork,
  startLoading,
  paperMaterial,
  artworkImage,
  artworkInfo,
  flowConfig,
  endLoading,
  context,
  matrix,
  editor,
  flow,
  setResizeParams,
  setPanelSizes,
  setScaleParams,
  setBgParams,
  processJob,
  requestImgBgRemoved,
  imageNotGenerated,
  setImageNotGenerated,
  resetArtworkInfo,
  resetArtworkImage,
  resetWhiteInkOptionsData,
  setWhiteInkOptionsNeeded,
  resizeParams,
  scaleParams,
  snapToEdgeEnabled,
  bgParams,
  isAuthorized,
  getAuthorizationStatus,
  saveToYourAccount,
}) => {
  const [actionChain, setActionChain] = useState(null);
  const activatedContext = useRef(null);
  const initialMatrix = useRef(matrix);
  const {
    ctx,
    design: {
      product: currentProductId,
    }
  } = matrix;
  const isMobileView = isMobileMode(ctx);
  const createdFromDielineShape = isCreatedFromDielineShape(context);

  const resetShapeIfNeeded = async () => {
    if (!createdFromDielineShape) return;

    const { projectData, project } = context;
    const {
      locale: { measurementSystem: units },
    } = flowConfig;
    const {
      ctx: { width, height },
      design: { product: productId },
    } = matrix;

    const newProjectData = await resetCustomSizedArtwork({
      projectData,
      project,
      productId,
      width,
      height,
      units,
    });
    const { projectLocation: { relativePath, storageId } = {}, product: { id: newProductId } = {} } = newProjectData;
    updateMatrixState(matrix, {
      design: {
        product: newProductId,
      },
      customize: {
        path: relativePath,
        storage: storageId,
      }
    });
  };

  const convertToTwips = useCallback(
    value => {
      const {
        locale: { measurementSystem = 'imperial' },
      } = flowConfig;

      return value ? toTwips(value, measurementSystem) : value;
    },
    [flowConfig],
  );

  const getWorkingAreaWarningHelpInfo = useCallback(workingAreaWarningCode => {
    const warningsList = getWorkingAreaWarningsList(flowConfig);
    return warningsList[workingAreaWarningCode];
  }, [flowConfig]);

  const showImagePpiWarning = useCallback(() => {
    const { key, helpSection, helpTopic } = getWorkingAreaWarningHelpInfo(WORKING_AREA_WARNING_STRETCHED_CODE);

    addWorkingAreaWarning({
      key,
      helpTopic,
      helpSection,
      onRemove: () => {
        const ppiErrorDismissed = sessionStorage.getItem('showPpiError') === 'Done';
        !ppiErrorDismissed && sessionStorage.setItem('showPpiError', 'Done');
      },
    });
  }, [addWorkingAreaWarning, getWorkingAreaWarningHelpInfo]);

  const showWarningByHints = useCallback(() => {
    const whiteSpotColorDetected = getWhiteSpotColorDetected(context);
    const createdFromDielineShape = isCreatedFromDielineShape(context);
    let warningCode;

    switch (true) {
      case createdFromDielineShape && whiteSpotColorDetected:
        warningCode = WORKING_AREA_WARNING_CUSTOM_SHAPE_HPI_WHITE_CODE;
        break;
      case createdFromDielineShape:
        warningCode = WORKING_AREA_WARNING_CUSTOM_SHAPE_CODE;
        break;
      case whiteSpotColorDetected:
        warningCode = WORKING_AREA_WARNING_HPI_WHITE_CODE;
        break;
      default:
        return;
    }

    const { key, helpSection, helpTopic } = getWorkingAreaWarningHelpInfo(warningCode);

    addWorkingAreaWarning({
      key,
      helpTopic,
      helpSection,
      onRemove: () => {
        const errorDismissed = sessionStorage.getItem(`showWarning-${warningCode}`) === 'Done';
        !errorDismissed && sessionStorage.setItem(`showWarning-${warningCode}`, 'Done');
      },
    });
  }, [addWorkingAreaWarning, context, getWorkingAreaWarningHelpInfo]);

  const removeImagePpiWarning = useCallback(() => {
    const { key: warningKey } = getWorkingAreaWarningHelpInfo(WORKING_AREA_WARNING_STRETCHED_CODE);
    removeWorkingAreaWarning(warningKey);
  }, [removeWorkingAreaWarning, getWorkingAreaWarningHelpInfo]);

  const showQAUUploadWarnings = useCallback(() => {
    const { warnings } = artworkInfo;

    const warningsList = getWorkingAreaWarningsList(flowConfig);
    const warningCodes = Object.keys(warningsList).map(Number);

    const uploadWarnings = warningCodes.map(warningCode => parseError(warnings, [warningCode])).filter(Boolean);
    uploadWarnings.forEach(({ code }) => addWorkingAreaWarning(warningsList[code]));
  }, [artworkInfo, addWorkingAreaWarning, flowConfig]);

  const showSizeChangedMessage = useCallback(({ width, height }) => {
    const measurementLocale = getCurrentMeasurementLocale(flowConfig);
    const currentFlow = isStickerFlow(flowConfig) ? 'sticker' : 'label';
    const key = `qau-${currentFlow}-requested-size-changed-${measurementLocale}`;
    const values = {
      width: roundNum(fromTwips(width, measurementLocale), 2),
      height: roundNum(fromTwips(height, measurementLocale), 2),
    };
    const messageCfg = { key, values, duration: 10000 };

    addWorkingAreaMessage(messageCfg);
  }, [addWorkingAreaMessage, flowConfig]);

  const imageGenerated = useCallback(
    ({ imageType }, imageName, type) => {
      const {
        editor: {
          selectedFields: [image],
        },
      } = flow;

      if (type !== imageType || !image) return;

      const { validator } = context;
      const { features } = flowConfig;

      const imagePpiErrors = validator.validateImagePpi(image);

      if (imagePpiErrors.length) {
        showImagePpiWarning();
      }

      const shouldShowQAUUploadWarning = features?.featureExistsAndEnabled('showQAUUploadWarning');
      shouldShowQAUUploadWarning && showQAUUploadWarnings();

      showWarningByHints();

      checkForWhiteIfNeeded(flowConfig, context, image, isMobileView, whiteInkOptionsNeeded => {
        setWhiteInkOptionsNeeded(whiteInkOptionsNeeded && !createdFromDielineShape);
        setImageNotGenerated(false);
      });
    },
    [
      flow,
      context,
      flowConfig,
      showQAUUploadWarnings,
      showWarningByHints,
      showSizeChangedMessage,
      isMobileView,
      showImagePpiWarning,
      setWhiteInkOptionsNeeded,
      setImageNotGenerated,
    ],
  );

  const updateActions = useCallback(() => {
    const {
      editor: { selectedFields },
    } = flow;

    const actions = actionChain.getActions(selectedFields);
    updateCustomizeActions(actions);
  }, [flow, actionChain, updateCustomizeActions]);

  const performAction = useCallback(
    (action, source) => {
      const { name: actionName = '', params: actionParams = {} } = action;
      const {
        editor: { selectedFields },
      } = flow;

      const performed = actionChain.performAction(selectedFields, actionName, actionParams, source);
      !performed && console.log(`${actionName} was not performed by chain of responsibility`);

      cleanupActions();
      updateActions();
    },
    [flow, actionChain, cleanupActions, updateActions],
  );

  const saveProject = useCallback(() => {
      const { projectData: { id } = {} } = context;
      !id && flow && flow.updateProject();
    }, [flow, context],
  );

  const autoSaveProject = useCallback(debounce(() => saveProject(), 10000), [flow, context]);

  const onSnapshotCreated = useCallback(() => {
    autoSaveProject();
    actionChain && updateActions();
  }, [actionChain, autoSaveProject, updateActions]);

  const onFieldChangePosition = () => {
    setScaleParams({ fitDisabled: isImageFitDisabled(flow) });
  };

  const validateImage = useCallback(() => {
    const { validator } = context;
    const {
      editor: {
        selectedFields: [image],
      },
    } = flow;

    const imagePpiErrors = validator.validateImagePpi(image);
    const ppiErrorDismissed = sessionStorage.getItem('showPpiError') === 'Done';

    imagePpiErrors.length && !ppiErrorDismissed ? showImagePpiWarning() : removeImagePpiWarning();
  }, [context, flow, showImagePpiWarning, removeImagePpiWarning]);

  const patchDeselectionHandler = transformTool => {
    transformTool.selectionHandlerLink.fn = function (e) {
      const me = this;
      const offset = me.coordinateTransformer.getPointerOffset(e);
      const field = me.editor.getFieldAtPos(offset.x, offset.y);
      const fieldEl = me.getFieldEl(e.target);

      if (!fieldEl && !field) {
        if (!e.shiftKey) return false;
        me.editTextBlock(null);
        me.cropImage(null);
        me.startSelection(offset.x, offset.y);
        return true;
      }

      if (e.shiftKey || e.ctrlKey) {
        me.editor.transformTool.selectFields([field], true);
        me.transformingEnabled() && me.startTranslation(offset.x, offset.y, e);
        return true;
      }

      return false;
    };
  };

  const { finishArtworkProject } = useSaveToYourAccount({
    flow,
    context,
    flowConfig,
    finishArtwork,
    processFinishedArtwork,
    isAuthorized,
    matrix,
    getAuthorizationStatus,
    setFooterConfig,
  });

  const createActionsChains = useCallback(
    context => {
      const { editor, chain: flowChain } = flow;
      const suppressChain = new DPO.bll.SuppressActionChain(context, flowConfig);
      const editorChain = new DPO.bll.EditorActionChain(context, editor, { itemNumberVisible: () => {} });
      const projectChain = new DPO.bll.ProjectActionChain(context);
      const splitChain = new SplitActionChain(context, editor);

      editorChain.nextChain = projectChain;
      flowChain.nextChain = editorChain;
      suppressChain.nextChain = flowChain;
      splitChain.nextChain = suppressChain;

      setActionChain(splitChain);
    },
    [flow, flowConfig],
  );

  const shouldImageScaleAccordingToPanel = useCallback(()=> {
    const { features } = flowConfig;
    return features.featureExistsAndEnabled('shouldImageScaleAccordingToPanel') || createdFromDielineShape;
  }, [flowConfig, createdFromDielineShape]);

  const getProductSizeCustomizationParams = useCallback(() => {
    const { features = {} } = flowConfig;
    const feature = features.getFeature('productSizeCustomization');
    const parseSize = value => (/^\d+(\.\d+)?x\d+(\.\d+)?$/.test(value) ? value.split('x').map(convertToTwips) : null);

    if (!feature) {
      return {
        disabled: true,
        product: { min: [0, 0], max: [Infinity, Infinity] },
        cut2image: { min: [0, 0], max: [Infinity, Infinity], ppi: 200 },
      };
    }

    const product = {
      min: parseSize(feature.getParamValue('product-min-size')) || [0, 0],
      max: parseSize(feature.getParamValue('product-max-size')) || [Infinity, Infinity],
    };
    const cut2image = {
      min: parseSize(feature.getParamValue('cut2image-min-size')) || product.min,
      max: parseSize(feature.getParamValue('cut2image-max-size')) || product.max,
      ppi: +feature.getParamValue('cut2image-ppi') || 200,
    };

    return { disabled: feature.disabled, product, cut2image };
  }, [flowConfig, convertToTwips]);

  const getSizeAndShapeFromUrl = useCallback(() => {
    const { width, height, shape } = ctx;

    return {
      width: convertToTwips(width),
      height: convertToTwips(height),
      shape,
    };
  }, [ctx, convertToTwips]);

  const updateShapeFromXmlIfNeeded = useCallback(shape => {
    if (ctx.shape) return;
    updateMatrixState(matrix, { ctx: { shape } });
  }, [matrix]);

  const resetUrlSizesIfNeeded = useCallback(() => {
    if (!isCustomShapeFlow(flowConfig)) return;

    updateMatrixState(matrix, {
      ctx: {
        width: 0,
        height: 0,
      },
    });
  }, [matrix, flowConfig, isCustomShapeFlow, updateMatrixState]);

  const initArtworkField = useCallback(
    (panel, append) => {
      const {
        editor: { transformTool },
      } = flow;
      const {
        projectTransformer: { selector },
      } = context;
      const fields = getPanelFields(panel, selector);
      transformTool.selectFields(fields, append);
    },
    [context, flow],
  );

  const validateMasterPanelSize = ({ width, height }, min = [0, 0], max = [Infinity, Infinity]) => {
    const minMax = (value, min, max) => Math.min(Math.max(min, value), max);
    const compare = (a, b) => (width < height ? a - b : b - a);
    min = min.sort(compare);
    max = max.sort(compare);

    let correctWidth = width;
    let correctHeight = height;

    const w = minMax(correctWidth, min[0], max[0]);
    correctHeight *= w / correctWidth;
    correctWidth = w;

    const h = minMax(correctHeight, min[1], max[1]);
    correctWidth *= h / correctHeight;
    correctHeight = h;

    correctWidth = minMax(correctWidth, min[0], max[0]);
    correctHeight = minMax(correctHeight, min[1], max[1]);

    return {
      width,
      height,
      correctWidth,
      correctHeight,
      valid: width === correctWidth && height === correctHeight,
    };
  };

  const autoZoom = useCallback(() => {
    const { editor } = flow;
    const { width: sidePanelWidth, height: sidePanelHeight } = getDOMElRect('qau-side-tool-panel');
    const { height: topPanelHeight } = getDOMElRect('qau-top-tool-panel');

    const padding = 50;
    const mobileVerticalAdjustment = sidePanelHeight * 0.5;

    editor.zoomPadding = {
      right: padding + (isMobileView ? 0 : sidePanelWidth),
      left: padding + (isMobileView ? 0 : sidePanelWidth),
      bottom: padding + (isMobileView ? mobileVerticalAdjustment : 0),
      top: padding + (isMobileView ? mobileVerticalAdjustment : topPanelHeight),
    };
    editor.configureViewPort();
    editor.updateViewPort();
    editor.autoZoom();
  }, [flow, isMobileView]);

  const resizeMasterPanel = useCallback(
    (width, height, {
      target = 'user',
      keepImgAspect,
      scaleAccordingToPanel,
    } = {}) => {
      const { disabled, cut2image, product } = getProductSizeCustomizationParams();
      if (disabled || !width && !height) return;

      const { editor } = flow;
      const {
        masterPanel: master,
        projectEditor: { pt },
      } = editor;

      const size = target === 'artwork' ? cut2image : product;
      const { correctWidth, correctHeight, valid } = validateMasterPanelSize({ width, height }, size.min, size.max);

      if (master.width === width && master.height === height && valid) return;

      startLoading();

      const scaleX = correctWidth / master.width;
      const scaleY = correctHeight / master.height;

      const scaleImageAccordingToPanel = scaleAccordingToPanel || shouldImageScaleAccordingToPanel();
      const shouldContentScale = target !== 'artwork';
      pt.scaleMasterPanel(master, scaleX, scaleY, shouldContentScale, scaleImageAccordingToPanel, keepImgAspect);

      editor.changePanel(editor.panel, false, null, true, true);
      initArtworkField(editor.panel);
      autoZoom();
      endLoading();
    },
    [flow, endLoading, startLoading, getProductSizeCustomizationParams, initArtworkField, autoZoom, shouldImageScaleAccordingToPanel],
  );

  const resizeArtworkImage = useCallback(
    () => {
      const { aspectRatioLocked: keepImgAspect } = scaleParams;
      const { prevSizes, selectedSizes } = resizeParams;
      const { editor } = flow;
      const {
        projectEditor: { pt },
        selectedFields,
      } = editor;

      const scaleX = prevSizes.width / selectedSizes.width;
      const scaleY = prevSizes.height / selectedSizes.height;
      const safeScaleMin = Math.min(scaleX, scaleY);
      pt.scaleFields(selectedFields, keepImgAspect ? safeScaleMin : scaleX, keepImgAspect ? safeScaleMin : scaleY, false, true);
    },
    [flow, resizeParams, scaleParams],
  );

  const setMasterPanelShapeFromImage = useCallback(
    async ({ artworkImage = {}, stickerSize, keepAspect = true, cfg = { silent: false } }) => {
      const {
        cut2image: { ppi },
        disabled: sizeCustomizationDisabled,
      } = getProductSizeCustomizationParams();

      if (sizeCustomizationDisabled) return;

      const { contourCut } = bgParams;
      const { keep, removed } = findRmbgImg(flow);
      const shapeImgField = contourCut ? removed : keep;
      const shapeImgSrc = shapeImgField?.source || artworkImage.name;

      const { editor } = flow;
      const {
        masterPanel: master,
        projectEditor: { pt },
      } = editor;
      const { source, width, height } = shapeImgField || {};
      const stickerImg = { source, width, height };

      if (!shapeImgField) {
        pt.clearFieldsOnPanel(master);
      }
      pt.beginUpdates();

      let lowResImage;

      try {
        startLoading();
        lowResImage = await requestLowResImage(shapeImgSrc, flow.projectData);
      } catch (errorImageUri) {
        // Shows error image on canvas if low resolution image polling timeout expired
        editor.createImage(errorImageUri, null, null, master.width, master.height);
        return;
      }

      try {
        startLoading();
        const { uri } = lowResImage;

        const image = await getImageByUrl(uri);
        const artworkOriginalSizes = getArtworkOriginalSizes(context) || lowResImage;
        const imageSize = getCustomStickerImgSize(master, stickerImg, shapeImgSrc, artworkOriginalSizes, stickerSize, keepAspect, ppi);
        image.width = imageSize.width;
        image.height = imageSize.height;

        pt.setMasterPanelShapeFromImage(
          new TraceImageToShape(),
          { master, image, ppi },
          shapeData => {
            const { offsetX, offsetY, shape, cutEdges } = shapeData;

            if (shapeImgField) {
              pt.resizeField(keep, imageSize.width, imageSize.height);
              pt.rotateField(keep, -keep.rotation);
              pt.setPosition(keep, -offsetX, -offsetY);
              if (removed) {
                  pt.resizeField(removed, imageSize.width, imageSize.height);
                  pt.rotateField(removed, -removed.rotation);
                  pt.setPosition(removed, -offsetX, -offsetY);
              }
            } else {
              editor.createImage(shapeImgSrc, -offsetX, -offsetY, imageSize.width, imageSize.height, {
                ...cfg,
                preventSizeProcessing: true,
              });
            }

            const { width: stickerWidth, height: stickerHeight } = getCustomStickerSize(stickerSize, shapeData, keepAspect);
            !cfg.silent && showSizeChangedMessage({ width: stickerWidth, height: stickerHeight });
            resizeMasterPanel(stickerWidth, stickerHeight, { scaleAccordingToPanel: true });
            editor.changePanel(editor.panel, false, null, true);
            setBgParams({
              contourCutInProgress: false,
              removeBgInProgress: false,
            });
            initArtworkField(editor.panel);
            pt.endUpdates();
            setPanelSizes(master);
            autoZoom();

            if (shape === 'rect' || !cutEdges) {
              addProjectWarning(flow, 'SHAPE_NOT_CREATED');
            } else {
              removeProjectWarning(flow, 'SHAPE_NOT_CREATED');
            }

            endLoading();
          },
        );
      } catch (res) {
        addProjectWarning(flow, 'SHAPE_NOT_CREATED');
        pt.endUpdates();
        autoZoom();
        endLoading();
      }
    },
    [
      flow,
      autoZoom,
      endLoading,
      setPanelSizes,
      getImageByUrl,
      getProductSizeCustomizationParams,
      initArtworkField,
      requestLowResImage,
      resizeMasterPanel,
      startLoading,
      bgParams,
    ],
  );

  const resizeMasterPanelIfNeeded = useCallback(
    (urlWidth, urlHeight, { disabled, cut2image: { ppi } }, { width: imgWidth, height: imgHeight }) => {
      if (disabled || !ppi || ppi <= 0) return;
      const { features } = flowConfig;
      const resizeArtworkOnInit = features.featureExistsAndEnabled('resizeArtworkOnInit');
      if (resizeArtworkOnInit) {
        const target = !urlWidth && !urlHeight ? 'artwork' : 'user';
        const width = urlWidth || inchesToTwips((imgWidth / ppi).toFixed(1));
        const height = urlHeight || inchesToTwips((imgHeight / ppi).toFixed(1));
        resizeMasterPanel(width, height, { target });
      }
    },
    [flowConfig, resizeMasterPanel],
  );

  const fitImage = useCallback(
    (image, panel, side) => {
      const { projectTransformer: pt } = context;
      const artworkFit = flowConfig.features.getFeature('artworkFit');
      const type = (artworkFit && artworkFit.getParamValue('type')) || 'none';
      pt.fitImageToSide(image, panel, side || type);
    },
    [context, flowConfig],
  );

  const createAndFitImage = useCallback(
    (mp, { name }, { width, height }, cfg) => {
      const { projectTransformer: pt } = context;
      let { keep: image } = findRmbgImg(flow);
      if (!image) {
        const { editor } = flow;
        image = editor.createImage(name, null, null, width, height, cfg);
      } else {
        pt.resizeField(image, width, height);
      }
      fitImage(image, mp);
    },
    [fitImage, flow],
  );

  const addArtworkImageToProject = useCallback(
    (artworkImage, artworkParams, shouldXmlUpdateImage = true, productSizeParams, isShapeChanged) => {
      if (!artworkImage || !artworkImage.highResolution || !shouldXmlUpdateImage) return;

      const { width, height, shape } = artworkParams;
      const { highResolution: image } = artworkImage;
      const {
        editor: {
          masterPanel: mp = {},
          context: { projectTransformer: pt },
        },
      } = flow;

      if (!isShapeChanged) {
        pt.clearFieldsOnPanel(mp);
      }

      if (isCustomShape(shape)) {
        const initialSizesProvided = !!(width && height);
        return setMasterPanelShapeFromImage({
          artworkImage,
          stickerSize: { width, height },
          cfg: { silent: !initialSizesProvided },
        });
      }

      pt.beginUpdates();
      resizeMasterPanelIfNeeded(width, height, productSizeParams, image);
      createAndFitImage(mp, artworkImage, image);
      pt.endUpdates();
    },
    [createAndFitImage, flow, getSizeAndShapeFromUrl, resizeMasterPanelIfNeeded, setMasterPanelShapeFromImage],
  );

  const correctAndFitArtworkImage = useCallback(() => {
      const { width, height, shape } = getSizeAndShapeFromUrl();
      const {
        editor: {
          masterPanel: mp,
          context: { projectTransformer: pt },
        },
      } = flow;
      const [image] = mp.images;
      const { source: name, hints } = image;
      const whiteTreatment = getWhiteTreatmentHint(hints)?.value;
      const originalName = getOriginalNameFromHint(hints)?.value;
      const keepOriginal = !!originalName;
      const cfg = { keepOriginal, originalName, whiteTreatment };

      if (isCustomShape(shape)) {
        const initialSizesProvided = !!(width && height);
        setMasterPanelShapeFromImage({ stickerSize: { width, height }, cfg: { silent: !initialSizesProvided } });
        return;
      }
      pt.beginUpdates();
      createAndFitImage(mp, { name }, image, cfg);
      pt.endUpdates();
    },
    [createAndFitImage, flow, getSizeAndShapeFromUrl, setMasterPanelShapeFromImage],
  );

  const setProjectRights = useCallback(() => {
    const { features } = flowConfig;
    const { projectRights } = context;

    projectRights.allowMoving = features.featureExistsAndEnabled('allowArtworkImageTransform') && !createdFromDielineShape;
  }, [context, flowConfig]);

  const initSnapToEdge = useCallback(() => {
    const {
      editor: {
        transformTool,
        masterPanel: { shape: masterPanelShape = '' },
      },
    } = flow;
    const isTransformAllowed = !flowConfig?.features.featureExistsAndDisabled('allowArtworkImageTransform') && !createdFromDielineShape;
    const isSnapControlVisible = isTransformAllowed && ['rect', 'ellipse'].includes(masterPanelShape);

    transformTool.snapAlignmentLines = isTransformAllowed && masterPanelShape === 'polygon';
    transformTool.snapToEdgeEnabled = isTransformAllowed && (snapToEdgeEnabled || masterPanelShape === 'polygon');
    transformTool.canHideTransformTool = true;
    transformTool.updateSnapLines();

    setSnapToEdgeVisible(isSnapControlVisible);
  }, [flow, flowConfig, snapToEdgeEnabled, setSnapToEdgeVisible]);

  const onZoomChanged = useCallback(
    (sender, zoom) => {
      setEditorZoom(zoom);
    },
    [setEditorZoom],
  );

  const restoreWhiteIfNeeded = useCallback(
    (keep, removed) => {
      const restoreWhiteKeep = keep && getOriginalSourceValue(flow, keep);
      const restoreWhiteRemoved = removed && getOriginalSourceValue(flow, removed);
      if (restoreWhiteKeep) {
        keep.source = restoreWhiteKeep;
        keep.hints = keep.hints.filter(({ name }) => name === 'removedBackgroundImg');
      }
      if (restoreWhiteRemoved) {
        removed.source = restoreWhiteRemoved;
        removed.hints = [];
      }
    },
    [],
  );

  const applyArtworkParams = async () => {
    const {
      selectedCornerType,
      selectedShape,
      shapeByCornerType,
      isShapeChanged,
      isCornerTypeChanged,
      isSizeChanged,
      selectedSizes: { width, height },
      flatShapeList,
    } = resizeParams;

    const newShape = shapeByCornerType || selectedShape;
    const isPolygonSizeChanged = isSizeChanged && isPolygonShape(newShape) &&
      !isCustomShape(newShape) && !checkShapeIsEquilateral(newShape) && !createdFromDielineShape;
    const {
      locale: { measurementSystem: units },
    } = flowConfig;
    const { bgVisibilityChanged, artworkBgVisible, contourCut, contourCutChanged } = bgParams;
    const { cornerRadius = -1 } = matrix.ctx;
    const { quoteAssetId } = matrix.design;
    const { productId } = flatShapeList.find(item => item.shape === newShape) || {};
    const unitsWidth = fromTwips(width, units);
    const unitsHeight = fromTwips(height, units);
    const defaultCornerRadius = getDefaultCornerRadius(width, height, selectedCornerType, flow);

    if (bgVisibilityChanged || contourCutChanged) {
      const { keep, removed } = findRmbgImg(flow);
      restoreWhiteIfNeeded(keep, removed);
      if (!removed) {
        const { storage, editor } = flow;
        const { projectData } = context;
        const {
          masterPanel: { images },
        } = editor;

        const [image] = images;

        setBgParams({
          removeBgInProgress: true,
          ...(contourCutChanged && { contourCutInProgress: true }),
        });

        const { jobId } = await requestImgBgRemoved({ projectData, image });
        const { imageDTO, isCorrupted } = await processJob({ projectData, jobId });
        const name = isCorrupted ? image.source : imageDTO.name;

        image.setHintValue('removedBackgroundImg', name);
        storage.requestLowImgIfNeeded(name);

        const { width, height, position: { x, y } } = image;
        editor.createImage(name, x, y, width, height, { preventSizeProcessing: true });

        setBgParams({ removedBgImgName: name });
      }
      setBgParams({
        ...(contourCutChanged && { contourCut: !contourCut, }),
        ...(bgVisibilityChanged && !isShapeChanged && !isCornerTypeChanged && !isPolygonSizeChanged && {
          artworkBgVisible: !artworkBgVisible,
          removeBgInProgress: false,
        }),
      });
    }

    if (isSizeChanged && !isShapeChanged && !isPolygonSizeChanged) {
      updateMatrixState(matrix, {
        ctx: {
          width: unitsWidth,
          height: unitsHeight,
        }
      });

      !bgVisibilityChanged && !contourCutChanged && resizeArtwork();
    }

    if (isShapeChanged || isCornerTypeChanged || isPolygonSizeChanged) {
      const { projectData, project } = context;
      const nextCornerRadius =
        (isShapeChanged || isCornerTypeChanged) && defaultCornerRadius !== undefined ? defaultCornerRadius : cornerRadius;
      const newProjectData = await applyCustomSizedShape({
        projectData,
        project,
        height: unitsHeight,
        width: unitsWidth,
        cornerRadius: nextCornerRadius,
        productId,
        quoteAssetId,
        units,
      });
      const { projectLocation: { relativePath, storageId }, product: { id: newProductId } } = newProjectData;

      updateMatrixState(matrix, {
        ctx: {
          width: unitsWidth,
          height: unitsHeight,
          shape: newShape,
        },
        design: {
          product: newProductId,
        },
        customize: {
          path: relativePath,
          storage: storageId
        }
      });

      flow.loadProject(newProjectData);
    }
  };

  const resizeArtwork = useCallback(() => {
      const {
        selectedShape,
        selectedSizes: { width, height },
        lockStickerAspectRatio: keepStickerAspect
      } = resizeParams;
      const { contourCutChanged } = bgParams;
      const { aspectRatioLocked: keepImgAspect } = scaleParams;

      const { editor: { masterPanel: mp } } = flow;
      if (
        mp.width.toFixed(4) === width?.toFixed(4) &&
        mp.height.toFixed(4) === height?.toFixed(4) &&
        !contourCutChanged
      ) return;

      const shouldDetectShape = isCustomShape(selectedShape) && !createdFromDielineShape;

      if (shouldDetectShape) {
        setMasterPanelShapeFromImage({
          stickerSize: { width, height },
          keepAspect: keepStickerAspect,
          cfg: { silent: true },
        });
        return;
      }
      resizeMasterPanel(width, height, { target: 'user', keepImgAspect });
      validateImage();
    },
    [context, resizeMasterPanel, validateImage, scaleParams, resizeParams, bgParams],
  );

  const finishArtworkIfNeeded = useCallback(() => {
    const prevView = sessionStorage.getItem('prevView');
    if (prevView === LOGIN_SIGNUP_VIEW && saveToYourAccount) {
      setFooterConfig({ backLocKey: 'qau-back-btn' });
      doFinishArtwork(isAuthorized);
      sessionStorage.setItem('prevView', '');
    }
  }, [doFinishArtwork, isAuthorized, saveToYourAccount, setFooterConfig]);

  const activateContext = useCallback(() => {
    const createdFromAmazonTransparency = getProjectHintValue(context, 'projectState') === 'createdFromAmazonTransparency';
    const projectShape = getProjectShape(context);
    const shouldXmlUpdateImage = !createdFromDielineShape && !createdFromAmazonTransparency;
    const { isShapeChanged, isSizeChanged, selectedShape } = resizeParams;
    const { bgVisibilityChanged, artworkBgVisible } = bgParams;
    sessionStorage.setItem('showPpiError', 'NotDone');
    updateShapeFromXmlIfNeeded(projectShape);

    flow.project = context.project;
    flow.storage = context.storage;
    flow.provider = context.provider;
    flow.projectData = context.projectData;

    context.storage.on('image:generated', imageGenerated, this);
    context.storage.on(
      'image:progress',
      () => {
        setImageNotGenerated(true);
      },
      this,
    );

    const { editor } = flow;
    editor.on('changed:zoom', onZoomChanged, this);
    editor.context = context;

    editor.transformTool.on(/.+:transform/, validateImage, this);
    editor.transformTool.on('changed:position', onFieldChangePosition, this);

    initSnapToEdge();
    patchDeselectionHandler(editor.transformTool);

    editor.gridVisible(false);
    editor.itemNumberVisible(false);

    createActionsChains(context);
    finishArtworkIfNeeded();
    const sizesAndShapeFromUrl = getSizeAndShapeFromUrl();
    const productSizeParams = getProductSizeCustomizationParams();

    if (!isShapeChanged && !createdFromDielineShape) {
      const { width, height } = sizesAndShapeFromUrl;
      const isPolygonSizeChanged = isSizeChanged && isPolygonShape(selectedShape) &&
        !isCustomShape(selectedShape) && !checkShapeIsEquilateral(selectedShape);

      if (isPolygonSizeChanged) {
        initArtworkField(editor.panel);
        resizeArtworkImage();
      }

      resizeMasterPanel(width, height);
    }

    addArtworkImageToProject(artworkImage, sizesAndShapeFromUrl, shouldXmlUpdateImage, productSizeParams, isShapeChanged);
    !artworkImage && isShapeChanged && correctAndFitArtworkImage();
    setProjectRights();
    initArtworkField(editor.panel);
    setScaleParams({ validateImage, fitDisabled: isImageFitDisabled(flow) });

    if (!productSizeParams.disabled && editor && editor.masterPanel) {
      setResizeParams({
        ...productSizeParams,
        createdFromDielineShape,
        isShapeChanged: false,
        isCornerTypeChanged: false,
        isSizeChanged: false,
        prevSizes: {},
      });
      setBgParams({
        removeBgInProgress: false,
        ...(bgVisibilityChanged && {
          artworkBgVisible: !artworkBgVisible,
        }),
      });
    }

    autoZoom();
    activatedContext.current = context;
  }, [
    flow,
    flowConfig,
    context,
    artworkImage,
    setScaleParams,
    setResizeParams,
    autoZoom,
    resizeParams,
    onZoomChanged,
    resizeArtwork,
    validateImage,
    imageGenerated,
    initSnapToEdge,
    setProjectRights,
    initArtworkField,
    onSnapshotCreated,
    createActionsChains,
    addArtworkImageToProject,
    getProductSizeCustomizationParams,
    finishArtworkIfNeeded,
  ]);

  const onProjectRestored = useCallback((sender, { projectLocation }) => navigateToView(CUSTOMIZE_VIEW, projectLocation), []);

  const onProjectCreated = useCallback(
    (sender, projectData) => {
      const {
        product: { id: productId },
        projectLocation,
      } = projectData;
      navigateToView(CUSTOMIZE_VIEW, projectLocation, productId);
      flow.loadProject(projectData);
    },
    [flow],
  );

  const onProjectLoaded = useCallback(
    (sender, project) => {
      createContext(flow, context, project).then(newContext => {
        updateContext(newContext);
      });
    },
    [flow, context, createContext, updateContext],
  );

  const processProjectLoad = useCallback(() => {
    if (createdFromDielineShape) {
      context.project = null;
      updateContext(context);
    }
    flow.projectData = null;

    loadProject(artworkInfo, flow, context, matrix);
  }, [flow, context, matrix, artworkInfo, updateContext]);

  const subscribeToFlowEvents = useCallback(() => {
    flow.on('loading:started', () => startLoading(), this);
    flow.on('loading:finished', () => endLoading(), this);
    flow.on('actions:changed', updateActions, this);
    flow.on('project:restored', onProjectRestored, this);
    flow.on('project:created', onProjectCreated, this);
    flow.on('project:loaded', onProjectLoaded, this);
    flow.on('error:occurred', processError, this);
    context.projectTransformer && context.projectTransformer.on('created:snapshot', onSnapshotCreated, this);
  }, [flow, startLoading, endLoading, onProjectRestored, onProjectCreated, onProjectLoaded, updateActions]);

  const unsubscribeFromFlowEvents = useCallback(() => {
    flow.un('loading:started', () => startLoading(), this);
    flow.un('loading:finished', () => endLoading(), this);
    flow.un('actions:changed', updateActions, this);
    flow.un('project:restored', onProjectRestored, this);
    flow.un('project:created', onProjectCreated, this);
    flow.un('project:loaded', onProjectLoaded, this);
    flow.un('error:occurred', processError, this);
    context.projectTransformer && context.projectTransformer.un('created:snapshot', onSnapshotCreated, this);
  }, [flow, startLoading, endLoading, onProjectRestored, onProjectCreated, onProjectLoaded, updateActions]);

  const bindShortcutActions = useCallback(() => {
    Mousetrap.reset();

    shortcutActions.forEach(shortcutAction => {
      const { shortcuts } = shortcutAction;

      Mousetrap.bind(shortcuts, e => {
        e.preventDefault();
        performAction(shortcutAction, 'shortcut');
      });
    });
  }, [shortcutActions, performAction]);

  const cancelProjectAutoSave = useCallback(() => {
    autoSaveProject?.cancel && autoSaveProject.cancel();
  }, [autoSaveProject]);

  const navigateToApplyCodes = useCallback(() => {
    saveProject();
    resetArtworkImage();
    navigate(APPLY_CODES_VIEW, {}, true);
  }, [context]);

  const navigateToWhiteInkOptions = useCallback(() => {
    saveProject();
    resetUrlSizesIfNeeded();
    resetArtworkImage();
    resetArtworkInfo();
    navigate(WHITE_INK_OPTIONS_VIEW, {}, true);
  }, [cancelProjectAutoSave, flow, resetUrlSizesIfNeeded]);

  const navigateToUpload = useCallback(() => {
    resetWhiteInkOptionsData();
    resetShapeIfNeeded().then(() => {
      resetUrlSizesIfNeeded();
      navigate(UPLOAD_ART_VIEW, {}, true);
    });
  }, [resetUrlSizesIfNeeded, resetShapeIfNeeded]);

  const doFinishArtwork = useCallback(
    saveToYourAccount => {
      saveToYourAccount && !isAuthorized && saveProject();
      cancelProjectAutoSave();
      finishArtworkProject(saveToYourAccount);
    },
    [cancelProjectAutoSave, finishArtworkProject, isAuthorized, saveProject],
  );

  const configureFooterButtons = useCallback(() => {
    const { paperMaterial } = context;
    const isNextStepApplyCodes = isApplyCodesViewAvailable(flowConfig);
    const isAvailableWhiteStep = !!paperMaterial || !!whiteInkOptionsNeeded;
    const isNextStep = isNextStepApplyCodes || (whiteInkOptionsNeeded && isAvailableWhiteStep);
    const isProcessing = imageNotGenerated || whiteInkOptionsNeeded === null || paperMaterial === undefined;

    let nextFn = () => doFinishArtwork(saveToYourAccount);

    if (whiteInkOptionsNeeded) nextFn = navigateToWhiteInkOptions;
    if (isNextStepApplyCodes) nextFn = navigateToApplyCodes;

    setFooterConfig({
      visible: true,
      onBack: navigateToUpload,
      onNext: nextFn,
      nextLocKey: isNextStep ? 'qau-next-btn' : 'qau-finish-btn',
      backHidden: isProcessing,
      nextHidden: isProcessing,
      nextDisabled: isProcessing,
      showSaveToMyAccount: isSaveUploadArtworkToMyAccount(flowConfig) && !whiteInkOptionsNeeded,
    });
  }, [
    ctx,
    context,
    paperMaterial,
    setFooterConfig,
    doFinishArtwork,
    navigateToApplyCodes,
    whiteInkOptionsNeeded,
    imageNotGenerated,
    flowConfig,
    saveToYourAccount,
  ]);

  const unsubscribeImgGenerate = useCallback(context => {
    context && context.storage.un('image:generated', imageGenerated, this);
  }, [imageGenerated]);

  const setCornerTypeFromProject = useCallback(() => {
    const {
      project: { panelSquaredCornerRadiusLimit },
      editor: {
        masterPanel: { cornerRadius: projectCornerRadius },
      },
    } = flow;
    let cornerType;

    switch (ctx.shape) {
      case 'rect':
      case 'square':
        cornerType = projectCornerRadius >= panelSquaredCornerRadiusLimit ? CORNER_TYPES.ROUNDED_CORNER : CORNER_TYPES.SQUARED_CORNER;
        break;
      case 'scallop_ellipse':
      case 'scallop_circle':
        cornerType = CORNER_TYPES.SCALLOPED_EDGE;
        break;
      case 'scallop_rect':
      case 'scallop_square':
        cornerType = CORNER_TYPES.SCALLOPED_CORNER;
        break;
      default:
        cornerType = null;
    }

    setResizeParams({ currentCornerType: cornerType, selectedCornerType: cornerType });
  }, [ctx.shape, flow, setResizeParams]);

  useEffect(() => {
    setImageNotGenerated(true);
    processProjectLoad();

    return () => {
      unsubscribeImgGenerate(context);
    };
  }, []);

  useEffect(() => {
    flow.editor !== editor && (flow.editor = editor);
  }, [editor, flow]);

  useEffect(() => {
    const { shouldApply } = resizeParams;

    if (shouldApply) {
      applyArtworkParams();
      setResizeParams({ shouldApply: false });
    }
  }, [resizeParams.shouldApply]);

  useEffect(() => {
    if (context.project && flow.editor) {
      const contextProductId = context.projectData.product.id;
      const isContextActivated = activatedContext.current === context;

      if (currentProductId !== contextProductId || isContextActivated) return;

      unsubscribeImgGenerate(activatedContext.current);
      activateContext();
    }
  }, [flowConfig, context.project, currentProductId]);

  useEffect(() => {
    if (!flow.project || !flow.editor) return;

    setCornerTypeFromProject();
  }, [context, flow, setCornerTypeFromProject]);

  useEffect(() => {
    if (context.project && flow.editor?.masterPanel) {
      if (createdFromDielineShape) {
        flow.project.bleedOffset = 0;
        flow.project.safeOffset = 0;
      }
      setProjectRights();
      initSnapToEdge();
      flow.editor.update();
      flow.editor.transformTool.update();
    }
  }, [flowConfig, context.project, flow.editor]);

  useEffect(() => {
    pendingAction && performAction(pendingAction);
  }, [pendingAction, performAction]);

  useEffect(() => {
    actionChain && updateActions();
  }, [actionChain, updateActions]);

  useEffect(() => {
    actionChain && bindShortcutActions();
    return Mousetrap.reset;
  }, [actionChain, bindShortcutActions]);

  useEffect(() => {
    subscribeToFlowEvents();
    return unsubscribeFromFlowEvents;
  }, [subscribeToFlowEvents, unsubscribeFromFlowEvents]);

  useEffect(() => {
    const { zoom = 1 } = editor || {};
    setEditorZoom(zoom);
  }, [editor, setEditorZoom]);

  const { masterPanel: { width: mpWidth = 0, height: mpHeight = 0 } = {} } = flow.editor || {};

  useEffect(() => {
    setPanelSizes({ width: mpWidth, height: mpHeight });
  }, [mpWidth, mpHeight, setPanelSizes]);

  useEffect(() => {
    configureFooterButtons();
  }, [configureFooterButtons]);

  useEffect(() => {
    const {
      removeBgInProgress,
    } = bgParams;
    if (removeBgInProgress || !flow || !flow.editor) return;

    const { editor: { masterPanel: { images } } } = flow;
    const image = images.find(i => i.visible);
    if (!image) return;

    checkForWhiteIfNeeded(flowConfig, context, image, isMobileView, whiteInkOptionsNeeded => {
      setWhiteInkOptionsNeeded(whiteInkOptionsNeeded && !createdFromDielineShape);
    });
  }, [bgParams.removeBgInProgress, bgParams.bgVisibilityChanged]);

  useEffect(() => {
    if (!flow || !flow.editor || !context.project) return;
    setProjectContourCutOption(context, bgParams.contourCut);
    resizeArtwork();
  }, [bgParams.contourCut]);

  useEffect(() => {
    if (!context.project || !artworkImage?.highResolution) return;

    setArtworkOriginalSizes(context, artworkImage);
  }, [context]);

  useEffect(() => {
    const { masterPanel } = flow?.editor || {};

    if (!masterPanel || resizeParams.isShapeChanged || imageNotGenerated || !createdFromDielineShape) return;

    let {
      ctx: { width: initialWidth, height: initialHeight },
    } = initialMatrix.current;

    initialWidth = convertToTwips(initialWidth);
    initialHeight = convertToTwips(initialHeight);

    const initialSizesProvided = !!(initialWidth && initialHeight);
    const { width: currentWidth, height: currentHeight } = masterPanel;
    const isSizeChanged = initialSizesProvided && (initialWidth !== currentWidth || initialHeight !== currentHeight);
    const showMessage = isSizeChanged || isCatalogSheetFlow(flowConfig);

    showMessage && showSizeChangedMessage({ width: currentWidth, height: currentHeight });
  }, [
    flow,
    imageNotGenerated,
    resizeParams.isShapeChanged,
    showSizeChangedMessage,
    flowConfig,
    createdFromDielineShape,
    convertToTwips,
  ]);

  useEffect(() => {
    getAuthorizationStatus();
  }, [getAuthorizationStatus]);

  return (
    <div id="qau-customize-view">
      <SideToolPanel />
      <TopToolPanel />
      <WorkingAreaMessages />
      <WorkingAreaWarnings />
      <WorkingArea isMobileView={isMobileView} />
    </div>
  );
};

const mapStateToProps = (
  { matrix, artwork, customize, whiteInkOptions, auth: { isAuthorized }, footer: { saveToYourAccount } },
  { context },
) => {
  const { actions, pendingAction, customizeData: { editor, imageNotGenerated, resizeParams, scaleParams, bgParams, snapToEdgeEnabled } = {} } = customize;
  const { project: artworkProject, image: artworkImage, info: artworkInfo, paperMaterial } = artwork;
  const { whiteInkOptionsNeeded } = whiteInkOptions;

  const shortcutActions = actions.filter(action => action.shortcuts?.length && !action.disabled);

  return {
    whiteInkOptionsNeeded,
    imageNotGenerated,
    shortcutActions,
    pendingAction,
    artworkProject,
    paperMaterial,
    artworkImage,
    artworkInfo,
    editor,
    matrix,
    resizeParams,
    scaleParams,
    snapToEdgeEnabled,
    bgParams,
    isAuthorized,
    saveToYourAccount,
  };
};

const mapDispatchToProps = {
  setWhiteInkOptionsNeeded,
  removeWorkingAreaWarning,
  applyCustomSizedShape,
  resetCustomSizedArtwork,
  updateCustomizeActions,
  processFinishedArtwork,
  addWorkingAreaWarning,
  addWorkingAreaMessage,
  resetCustomizeData,
  requestLowResImage,
  setImageNotGenerated,
  resetArtworkInfo,
  resetArtworkImage,
  resetWhiteInkOptionsData,
  setSnapToEdgeVisible,
  requestImgBgRemoved,
  processJob,
  setEditorZoom,
  setFooterConfig,
  setResizeParams,
  setScaleParams,
  setBgParams,
  cleanupActions,
  finishArtwork,
  setPanelSizes,
  createContext,
  getImageByUrl,
  processError,
  startLoading,
  endLoading,
  getAuthorizationStatus,
};

CustomizeView.propTypes = {
  matrix: PropTypes.object,
  editor: PropTypes.object,
  artworkInfo: PropTypes.object,
  artworkImage: PropTypes.object,
  paperMaterial: PropTypes.object,
  pendingAction: PropTypes.object,
  artworkProject: PropTypes.object,
  shortcutActions: PropTypes.array,
  flow: PropTypes.object.isRequired,
  resizeParams: PropTypes.object.isRequired,
  scaleParams: PropTypes.object.isRequired,
  snapToEdgeEnabled: PropTypes.bool.isRequired,
  context: PropTypes.object.isRequired,
  endLoading: PropTypes.func.isRequired,
  startLoading: PropTypes.func.isRequired,
  flowConfig: PropTypes.object.isRequired,
  processError: PropTypes.func.isRequired,
  finishArtwork: PropTypes.func.isRequired,
  updateContext: PropTypes.func.isRequired,
  createContext: PropTypes.func.isRequired,
  cleanupActions: PropTypes.func.isRequired,
  setFooterConfig: PropTypes.func.isRequired,
  getImageByUrl: PropTypes.func.isRequired,
  setEditorZoom: PropTypes.func.isRequired,
  setScaleParams: PropTypes.func.isRequired,
  setResizeParams: PropTypes.func.isRequired,
  setSnapToEdgeVisible: PropTypes.func.isRequired,
  resetArtworkInfo: PropTypes.func.isRequired,
  resetArtworkImage: PropTypes.func.isRequired,
  resetWhiteInkOptionsData: PropTypes.func.isRequired,
  setImageNotGenerated: PropTypes.func.isRequired,
  requestLowResImage: PropTypes.func.isRequired,
  resetCustomizeData: PropTypes.func.isRequired,
  addWorkingAreaWarning: PropTypes.func.isRequired,
  addWorkingAreaMessage: PropTypes.func.isRequired,
  removeWorkingAreaWarning: PropTypes.func.isRequired,
  processFinishedArtwork: PropTypes.func.isRequired,
  updateCustomizeActions: PropTypes.func.isRequired,
  setWhiteInkOptionsNeeded: PropTypes.func.isRequired,
  getAuthorizationStatus: PropTypes.func.isRequired,
  isAuthorized: PropTypes.bool.isRequired,
  saveToYourAccount: PropTypes.bool.isRequired,
};

export default inject('flow', 'context', 'flowConfig', 'updateContext')(connect(mapStateToProps, mapDispatchToProps)(CustomizeView));
